import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Search, MessageSquare, Mic, Image as ImageIcon, Zap, Bell, ShieldOff, ArrowUpRight } from "lucide-react";
import { useT } from "../i18n";
import { href, USE_CASES, SCREENS, PLANS, formatPrice, savingVs, STORES } from "../content/shared";
import { MaskHeadline, Reveal, Screen, Plate, StoreBadges, SectionHead, ArrowLink, Marquee, FaqList, QR } from "../components/kit";
import { Demo, AppPhone } from "../components/Demo";
import D from "../content/demos";

const Hero = () => {
  const { lang, t } = useT();
  const h = t.home;
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPhoto = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "12%"]);
  const yScreen = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -90]);
  return (
    <section ref={ref} className="container-x grid items-center gap-12 pb-16 pt-10 md:pt-16 lg:grid-cols-[1fr_1.1fr] lg:pb-24" data-testid="home-hero">
      <div>
        <Reveal><p className="eyebrow mb-6">{h.eyebrow}</p></Reveal>
        <MaskHeadline lines={h.h1} testid="home-hero-title" />
        <Reveal delay={0.3}><p className="lead mt-6">{h.lead}</p></Reveal>
        <Reveal delay={0.4} className="mt-8 flex flex-wrap gap-3">
          <Link to={href(lang, "download")} className="btn-primary h-12 px-6" data-testid="hero-download-btn">{h.ctaPrimary}</Link>
          <Link to={href(lang, "how")} className="btn-secondary h-12 px-6" data-testid="hero-how-btn">{h.ctaSecondary}</Link>
        </Reveal>
        <Reveal delay={0.5}><p className="mt-6 max-w-md text-sm text-muted-foreground">{h.note}</p></Reveal>
      </div>
      <div className="relative mx-auto mt-16 w-full max-w-[640px] sm:mt-0 lg:max-w-none">
        <div className="relative ml-auto aspect-[4/5] w-[86%] overflow-hidden rounded-[2rem] sm:aspect-[5/5] lg:aspect-[4/4.4]">
          <motion.img style={{ y: yPhoto }} src="/images/street.webp" alt={h.photoAlt} className="absolute inset-0 h-[115%] w-full object-cover" fetchpriority="high" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#171940]/50 to-transparent" />
        </div>
        <motion.div style={{ y: yScreen }} className="absolute -bottom-8 left-0 w-[62%] max-w-[290px] sm:w-[48%]">
          <AppPhone plate={USE_CASES[0].plate} testid="hero-app-phone" />
        </motion.div>
      </div>
    </section>
  );
};

const Story = () => {
  const { t } = useT();
  const s = t.home.story;
  return (
    <section className="bg-[#171940] text-white" data-testid="home-story">
      <div className="container-x section grid gap-14 lg:grid-cols-[1fr_1fr]">
        <Reveal>
          <p className="mb-5 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-white/70"><span className="h-px w-5 bg-[#F5C842]" />{s.eyebrow}</p>
          <h2 className="max-w-xl text-white">{s.h2}</h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-white/80 md:text-lg">{s.text}</p>
          <div className="mt-10 overflow-hidden rounded-2xl">
            <img src="/images/uc-blocked.webp" alt={s.photoAlt} loading="lazy" className="aspect-[16/10] w-full object-cover" />
          </div>
        </Reveal>
        <ol className="relative space-y-8 border-l border-white/20 pl-8 lg:mt-24">
          {s.steps.map(([time, text], i) => (
            <Reveal as="li" key={i} delay={i * 0.08} className="relative">
              <span className="absolute -left-[2.35rem] top-1.5 h-3 w-3 rounded-full border-2 border-[#F5C842] bg-[#171940]" />
              <p className="font-mono text-sm text-[#F5C842]">{time}</p>
              <p className="mt-1 text-base text-white/90 md:text-lg">{text}</p>
            </Reveal>
          ))}
          <Reveal as="li" className="text-foreground"><Demo scenario="scene" plate={USE_CASES[0].plate} className="max-w-sm" testid="story-demo-scene" captionClass="text-white/70" /></Reveal>
        </ol>
      </div>
    </section>
  );
};

const Cases = () => {
  const { lang, t } = useT();
  const h = t.home;
  const items = t.useCases.items;
  const [first, ...rest] = USE_CASES;
  return (
    <section className="container-x section" data-testid="home-use-cases">
      <SectionHead eyebrow={h.casesEyebrow} title={h.casesH2} lead={h.casesLead} />
      <div className="mt-14 grid gap-10 lg:grid-cols-[1.1fr_1fr]">
        <Reveal>
          <Link to={href(lang, "useCases", first.slug)} className="group block" data-testid={`home-case-${first.slug}`}>
            <div className="overflow-hidden rounded-[1.5rem]"><img src={first.img} alt={items[first.slug].title} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" /></div>
            <h3 className="mt-6 text-2xl md:text-3xl">{items[first.slug].title}</h3>
            <p className="mt-2 max-w-lg text-muted-foreground">{items[first.slug].short}</p>
          </Link>
        </Reveal>
        <ul className="border-t border-border">
          {rest.map((u, i) => (
            <Reveal as="li" key={u.slug} delay={i * 0.04} className="border-b border-border">
              <Link to={href(lang, "useCases", u.slug)} className="group grid grid-cols-[2.5rem_1fr_auto] items-center gap-4 py-5" data-testid={`home-case-${u.slug}`}>
                <span className="font-mono text-sm text-muted-foreground">{String(i + 2).padStart(2, "0")}</span>
                <span>
                  <span className="block font-display text-lg font-semibold transition-colors group-hover:text-primary md:text-xl">{items[u.slug].title}</span>
                  <span className="mt-0.5 block text-sm text-muted-foreground">{items[u.slug].short}</span>
                </span>
                <ArrowUpRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden />
              </Link>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
};

const Process = () => {
  const { lang, t } = useT();
  const h = t.home;
  const screens = [SCREENS.register, SCREENS.search, SCREENS.chat];
  return (
    <section className="border-y border-border bg-surface" data-testid="home-process">
      <div className="container-x section">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <SectionHead eyebrow={h.processEyebrow} title={h.processH2} lead={h.processLead} />
          <ArrowLink to={href(lang, "how")} testid="home-process-link">{h.processLink}</ArrowLink>
        </div>
        <ol className="mt-14 grid gap-10 md:grid-cols-3">
          {h.steps.map((s, i) => (
            <Reveal as="li" key={i} delay={i * 0.1}>
              <div className="mx-auto max-w-[260px] md:mx-0"><Screen src={screens[i]} alt={s.alt} /></div>
              <p className="mt-6 font-mono text-sm text-primary">0{i + 1}</p>
              <h3 className="mt-1 text-xl">{s.t}</h3>
              <p className="mt-2 max-w-sm text-muted-foreground">{s.d}</p>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
};

const ICONS = [Search, MessageSquare, Mic, ImageIcon, Zap, Bell, ShieldOff];
const SPANS = ["md:col-span-4", "md:col-span-2", "md:col-span-2", "md:col-span-2", "md:col-span-2", "md:col-span-3", "md:col-span-3"];
const Features = () => {
  const { lang, t } = useT();
  const h = t.home;
  return (
    <section className="container-x section" data-testid="home-features">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <SectionHead eyebrow={h.featuresEyebrow} title={h.featuresH2} lead={h.featuresLead} />
        <ArrowLink to={href(lang, "product")} testid="home-features-link">{h.featuresLink}</ArrowLink>
      </div>
      <div className="mt-14 grid gap-4 md:grid-cols-6">
        {h.features.map((f, i) => {
          const Icon = ICONS[i];
          return (
            <Reveal key={i} delay={(i % 3) * 0.06} className={`card flex flex-col justify-between gap-8 p-6 md:p-8 ${SPANS[i]} ${i === 0 ? "bg-primary-soft" : ""}`}>
              <div className="flex items-start justify-between gap-4">
                <Icon className="h-5 w-5 text-primary" aria-hidden />
                {i === 0 && <Plate text="AB 123 CD" size="sm" />}
              </div>
              <div>
                <h3 className={i === 0 ? "text-2xl md:text-3xl" : "text-xl"}>{f.t}</h3>
                <p className="mt-2 max-w-md text-muted-foreground">{f.d}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

const Modes = () => {
  const { lang } = useT();
  const d = D[lang];
  const list = [["photo", "FR 330 BN"], ["voice", "MI 905 XZ"], ["audio", "TR 482 KL"], ["video", "SQ 118 RE"]];
  return (
    <section className="border-y border-border bg-surface" data-testid="home-modes">
      <div className="container-x section">
        <SectionHead eyebrow={d.modesEyebrow} title={d.modesH2} lead={d.modesLead} />
        <div className="mt-14 grid gap-10 sm:grid-cols-2 xl:grid-cols-4">
          {list.map(([k, plate], i) => (
            <Reveal key={k} delay={i * 0.08}>
              <p className="mb-4 flex items-center gap-2 font-display text-lg font-semibold"><span className="font-mono text-sm text-primary">0{i + 1}</span>{d.modes[i]}</p>
              <Demo scenario={k} plate={plate} className="mx-auto max-w-[320px] sm:mx-0" testid={`home-demo-${k}`} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Privacy = () => {
  const { lang, t } = useT();
  const h = t.home;
  return (
    <section className="bg-surface-2" data-testid="home-privacy">
      <div className="container-x section grid items-center gap-14 lg:grid-cols-[1fr_1fr]">
        <div>
          <SectionHead eyebrow={h.privacyEyebrow} title={h.privacyH2} lead={h.privacyLead} />
          <dl className="mt-10 divide-y divide-border border-y border-border">
            {h.privacyPoints.map((p, i) => (
              <Reveal key={i} className="grid gap-1 py-5 sm:grid-cols-[14rem_1fr] sm:gap-6">
                <dt className="font-display font-semibold">{p.t}</dt>
                <dd className="text-muted-foreground">{p.d}</dd>
              </Reveal>
            ))}
          </dl>
          <ArrowLink to={href(lang, "safety")} className="mt-8" testid="home-privacy-link">{h.privacyLink}</ArrowLink>
        </div>
        <Reveal className="relative">
          <div className="overflow-hidden rounded-[1.75rem]"><img src="/images/garage.webp" alt={h.privacyPhotoAlt} loading="lazy" className="aspect-[4/5] w-full object-cover sm:aspect-[5/5]" /></div>
          <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-border bg-surface p-5 sm:right-auto sm:w-80">
            <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">{h.seesTitle}</p>
            <ul className="mt-3 space-y-2 text-sm">
              {h.sees.map(([ok, txt], i) => (
                <li key={i} className="flex gap-2"><span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${ok ? "bg-success" : "bg-danger"}`} />{txt}</li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const PricingTeaser = () => {
  const { lang, t } = useT();
  const h = t.home;
  return (
    <section className="container-x section" data-testid="home-pricing">
      <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr] lg:items-end">
        <SectionHead eyebrow={h.pricingEyebrow} title={h.pricingH2} lead={h.pricingLead} />
        <div className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-3">
          {PLANS.map((p) => (
            <div key={p.id} className="bg-surface p-6" data-testid={`home-plan-${p.id}`}>
              <p className="text-sm text-muted-foreground">{t.pricing.names[p.id]}</p>
              <p className="mt-2 font-display text-3xl font-semibold">{formatPrice(p.price, lang)}</p>
              <p className="text-sm text-muted-foreground">{t.pricing.per[p.id]}</p>
              {p.id === "yearly" && <p className="mt-3 text-xs font-medium text-primary">{t.pricing.saveYearly.replace("{n}", savingVs(p, "monthly"))}</p>}
            </div>
          ))}
        </div>
      </div>
      <ArrowLink to={href(lang, "pricing")} className="mt-8" testid="home-pricing-link">{h.pricingLink}</ArrowLink>
    </section>
  );
};

const Faq = () => {
  const { lang, t } = useT();
  const h = t.home;
  return (
    <section className="border-t border-border" data-testid="home-faq">
      <div className="container-x section grid gap-12 lg:grid-cols-[1fr_1.6fr]">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <SectionHead eyebrow={h.faqEyebrow} title={h.faqH2} />
          <ArrowLink to={href(lang, "help")} className="mt-6" testid="home-faq-link">{h.faqLink}</ArrowLink>
        </div>
        <FaqList items={h.faq} prefix="home-faq" />
      </div>
    </section>
  );
};

export const DownloadBand = () => {
  const { lang, t } = useT();
  const h = t.home;
  return (
    <section className="container-x pb-20" data-testid="download-band">
      <div className="grid gap-10 overflow-hidden rounded-[2rem] bg-[#171940] p-8 text-white md:p-14 lg:grid-cols-[1.4fr_1fr] lg:items-center">
        <div>
          <h2 className="max-w-xl text-white">{h.downloadH2}</h2>
          <p className="mt-5 max-w-lg text-white/80">{h.downloadLead}</p>
          <StoreBadges className="mt-8" prefix="band" />
          <Link to={href(lang, "download")} className="mt-6 inline-block text-sm text-white/80 underline-offset-4 hover:underline" data-testid="band-download-page-link">{h.downloadLink}</Link>
        </div>
        <div className="hidden justify-end gap-6 text-white md:flex">
          <QR url={STORES.ios} label="App Store" testid="band-qr-ios" />
          <QR url={STORES.android} label="Google Play" testid="band-qr-android" />
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const { t } = useT();
  return (
    <>
      <Hero />
      <Marquee items={t.home.marquee} />
      <Story />
      <Cases />
      <Process />
      <Features />
      <Modes />
      <Privacy />
      <PricingTeaser />
      <Faq />
      <DownloadBand />
    </>
  );
}
